import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, FileUp, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { OverlappedPressureChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import {
  analyzeSIndexCsv,
  applySIndexBreathEdits,
  buildOverlappedPressureData,
  buildSIndexEstimationSummary,
  buildSIndexInterpretation,
  getDefaultSIndexReferenceKey,
  getSIndexReferenceOptions,
  sanitizeMetricsForStorage,
  sanitizePlotBreathsForStorage,
  toFiniteNumber,
  toLocalDateTimeKey,
  type RespiratoryCsvFileMeta,
  type RespiratoryMusclePatientContext,
  type SIndexAnalysis,
  type SIndexReferenceKey,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';

interface SIndexTestProps {
  initial?: (SIndexResultPayload & { chartImages?: Record<string, string> }) | null;
  patientContext?: RespiratoryMusclePatientContext;
  referencePimaxMax?: number | null;
  referenceHandgripKg?: number | null;
  onCancel?: () => void;
  onNext: (result: SIndexResultPayload & { chartImages?: Record<string, string> }) => void;
}

export type { SIndexResultPayload };

const parseManualValue = (value: string): number | null => {
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildInitialMeasurements = (initial?: SIndexResultPayload | null): string[] => {
  if (Array.isArray(initial?.measurements) && initial.measurements.length) {
    return initial.measurements.map((value) => String(value).replace('.', ','));
  }
  return ['', '', ''];
};

const buildInitialAnalysis = (initial?: SIndexResultPayload | null): SIndexAnalysis | null => {
  if (!initial?.metrics?.length || !initial.plotBreaths?.length) return null;
  return {
    waveforms: initial.plotBreaths,
    metrics: initial.metrics,
    plotBreaths: initial.plotBreaths,
    maxPressure: toFiniteNumber(initial.maxPressureRaw) ?? Math.max(...initial.metrics.map((metric) => metric.peakPressure)),
  };
};

const exportElement = async (element: HTMLElement): Promise<string> => {
  const html2canvas = (await import('html2canvas')).default;
  const previousWidth = element.style.width;
  const previousMaxWidth = element.style.maxWidth;
  element.style.width = '760px';
  element.style.maxWidth = 'none';
  try {
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: 760,
      windowWidth: 900,
    });
    return canvas.toDataURL('image/png');
  } finally {
    element.style.width = previousWidth;
    element.style.maxWidth = previousMaxWidth;
  }
};

export default function SIndexTest({
  initial = null,
  patientContext,
  referencePimaxMax,
  referenceHandgripKg,
  onCancel,
  onNext,
}: SIndexTestProps) {
  const initialAnalysis = buildInitialAnalysis(initial);
  const [measurements, setMeasurements] = useState<string[]>(buildInitialMeasurements(initial));
  const [analysis, setAnalysis] = useState<SIndexAnalysis | null>(initialAnalysis);
  const [originalAnalysis, setOriginalAnalysis] = useState<SIndexAnalysis | null>(initialAnalysis);
  const [fileMeta, setFileMeta] = useState<RespiratoryCsvFileMeta | null>(initial?.file ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBreathNumbers, setSelectedBreathNumbers] = useState<number[]>(initial?.selectedBreaths ?? []);
  const [breathEdits, setBreathEdits] = useState<Record<number, { trimStart: number; trimEnd: number }>>({});
  const [editingBreath, setEditingBreath] = useState<number | null>(initialAnalysis?.plotBreaths[0]?.breathNumber ?? null);
  const [selectedReferenceEquation, setSelectedReferenceEquation] = useState<SIndexReferenceKey>(
    initial?.referenceEquation ?? getDefaultSIndexReferenceKey(),
  );
  const [manualHandgripInput, setManualHandgripInput] = useState<string>('');
  const [exportingCharts, setExportingCharts] = useState(false);
  const chartRef = useRef<HTMLDivElement | null>(null);

  const allBreathNumbers = useMemo(() => analysis?.plotBreaths.map((breath) => breath.breathNumber) ?? [], [analysis]);
  const activeBreathNumbers = selectedBreathNumbers.length ? selectedBreathNumbers : allBreathNumbers;
  const visibleBreaths = useMemo(
    () => (analysis ? analysis.plotBreaths.filter((breath) => activeBreathNumbers.includes(breath.breathNumber)) : []),
    [activeBreathNumbers, analysis],
  );
  const overlappedPressureData = useMemo(() => buildOverlappedPressureData(visibleBreaths), [visibleBreaths]);
  const referenceOptions = useMemo(() => getSIndexReferenceOptions(), []);
  const measurementValues = useMemo(
    () => measurements.map(parseManualValue).filter((value): value is number => value != null),
    [measurements],
  );
  const manualMax = measurementValues.length ? Math.max(...measurementValues) : null;

  const manualHandgripKg = useMemo(() => {
    if (!manualHandgripInput.trim()) return null;
    const parsed = Number(manualHandgripInput.replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [manualHandgripInput]);
  const effectiveReferenceHandgripKg =
    manualHandgripKg ?? toFiniteNumber(referenceHandgripKg) ?? toFiniteNumber(initial?.referenceHandgripKg);

  const estimationSummary = useMemo(
    () => {
      const estimationMetrics = analysis
        ? analysis.metrics
        : measurementValues.map((value) => ({
            peakPressure: value,
            peakFlow: 0,
            peakSIndex: value,
          }));

      if (!estimationMetrics.length) return initial?.summary ?? null;

      return buildSIndexEstimationSummary(estimationMetrics, {
        referencePimaxMax,
        referenceHandgripKg: effectiveReferenceHandgripKg,
        selectedReferenceEquation,
        patientAge: patientContext?.ageYears,
        patientSex: patientContext?.sex ?? null,
        patientHeight: patientContext?.heightCm,
        patientWeight: patientContext?.weightKg,
      });
    },
    [
      analysis,
      effectiveReferenceHandgripKg,
      initial?.summary,
      measurementValues,
      patientContext?.ageYears,
      patientContext?.heightCm,
      patientContext?.weightKg,
      patientContext?.sex,
      referencePimaxMax,
      selectedReferenceEquation,
    ],
  );
  const effectiveBreathsAnalyzed = analysis?.metrics.length ?? measurementValues.length;

  const interpretation = useMemo(
    () =>
      buildSIndexInterpretation({
        sIndexValue: estimationSummary?.maxPressure ?? null,
        breathsAnalyzed: effectiveBreathsAnalyzed || estimationSummary?.breathsAnalyzed || null,
        referencePimaxMax: referencePimaxMax ?? estimationSummary?.estimation?.referencePimaxMax ?? null,
        predictedSIndex: estimationSummary?.predictedSIndex ?? null,
        percentPredicted: estimationSummary?.percentPredicted ?? null,
        lln: estimationSummary?.llnSIndex ?? null,
        belowLLN: estimationSummary?.belowLLN ?? null,
        referenceLabel: estimationSummary?.referenceLabel ?? null,
      }),
    [effectiveBreathsAnalyzed, estimationSummary, referencePimaxMax],
  );
  const canFinish = Boolean(estimationSummary && (analysis || (measurementValues.length >= 3 && manualMax != null)));

  useEffect(() => {
    if (!analysis?.plotBreaths.length) {
      setEditingBreath(null);
      return;
    }
    const exists = editingBreath != null && analysis.plotBreaths.some((breath) => breath.breathNumber === editingBreath);
    if (!exists) setEditingBreath(analysis.plotBreaths[0].breathNumber);
  }, [analysis, editingBreath]);

  useEffect(() => {
    if (!originalAnalysis) return;
    setAnalysis(Object.keys(breathEdits).length ? applySIndexBreathEdits(originalAnalysis, breathEdits) : originalAnalysis);
  }, [breathEdits, originalAnalysis]);

  const updateMeasurement = (index: number, value: string) => {
    setMeasurements((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const clearImportedFile = () => {
    setAnalysis(null);
    setOriginalAnalysis(null);
    setFileMeta(null);
    setError(null);
    setSelectedBreathNumbers([]);
    setBreathEdits({});
    setEditingBreath(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = analyzeSIndexCsv(typeof reader.result === 'string' ? reader.result : '');
        setAnalysis(parsed);
        setOriginalAnalysis(parsed);
        setSelectedBreathNumbers(parsed.plotBreaths.map((breath) => breath.breathNumber));
        setBreathEdits({});
        setEditingBreath(parsed.plotBreaths[0]?.breathNumber ?? null);
        setFileMeta({ name: file.name, size: file.size, importedAt: toLocalDateTimeKey(new Date()) });
      } catch (caught) {
        console.error('Erro ao processar S-Index:', caught);
        clearImportedFile();
        setError(caught instanceof Error ? caught.message : 'Não foi possível processar o arquivo.');
      } finally {
        setLoading(false);
        event.target.value = '';
      }
    };
    reader.onerror = () => {
      setLoading(false);
      setError('Erro ao ler o arquivo.');
      event.target.value = '';
    };
    reader.readAsText(file, 'utf-8');
  };

  const toggleBreathSelection = (breathNumber: number) => {
    setSelectedBreathNumbers((current) => {
      const base = current.length ? current : allBreathNumbers;
      if (base.includes(breathNumber)) return base.length <= 1 ? base : base.filter((value) => value !== breathNumber);
      return [...base, breathNumber];
    });
  };

  const updateBreathEdit = (breathNumber: number, updates: Partial<{ trimStart: number; trimEnd: number }>) => {
    setBreathEdits((current) => ({
      ...current,
      [breathNumber]: { ...(current[breathNumber] || { trimStart: 0, trimEnd: 0 }), ...updates },
    }));
  };

  const resetBreathEdit = (breathNumber: number) => {
    setBreathEdits((current) => {
      const next = { ...current };
      delete next[breathNumber];
      return next;
    });
  };

  const handleFinish = async () => {
    if (!estimationSummary) {
      toast.error('Registre ao menos 3 medidas válidas ou importe um CSV/TXT válido para concluir.');
      return;
    }
    if (!analysis && (measurementValues.length < 3 || manualMax == null)) {
      toast.error('Registre ao menos 3 medidas válidas para concluir.');
      return;
    }

    const payloadBase: SIndexResultPayload = {
      summary: {
        ...estimationSummary,
        interpretation: interpretation.lines,
      },
      maxPressure: estimationSummary.maxPressure,
      maxPressureRaw: estimationSummary.maxPressureRaw,
      referenceEquation: estimationSummary.referenceEquation,
      referenceHandgripKg: estimationSummary.referenceHandgripKg,
      predictedSIndex: estimationSummary.predictedSIndex,
      percentPredicted: estimationSummary.percentPredicted,
      llnSIndex: estimationSummary.llnSIndex,
      belowLLN: estimationSummary.belowLLN,
      estimation: estimationSummary.estimation,
      ...(analysis ? {} : { measurements: measurementValues }),
      metrics: analysis ? sanitizeMetricsForStorage(analysis.metrics) : [],
      plotBreaths: analysis ? sanitizePlotBreathsForStorage(analysis.plotBreaths) : [],
      selectedBreaths: analysis ? activeBreathNumbers : [],
      patientAge: patientContext?.ageYears ?? null,
      patientSex: patientContext?.sex ?? null,
      patientHeight: patientContext?.heightCm ?? null,
      patientWeight: patientContext?.weightKg ?? null,
      patientContext,
      file: analysis ? fileMeta ?? undefined : undefined,
      finishedAt: toLocalDateTimeKey(new Date()),
    };

    if (!analysis) {
      onNext(payloadBase);
      return;
    }

    setExportingCharts(true);
    try {
      const chartImages: Record<string, string> = {};
      if (chartRef.current) chartImages.pressure_overlapped = await exportElement(chartRef.current);
      onNext({
        ...payloadBase,
        ...(Object.keys(chartImages).length ? { chartImages } : {}),
      });
    } catch (caught) {
      console.error('Erro ao exportar gráfico S-Index:', caught);
      toast.error('Não foi possível exportar o gráfico.');
    } finally {
      setExportingCharts(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-indigo-50 p-3 text-indigo-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">S-Index</p>
          <p className="text-sm opacity-90">Registre no mínimo 3 medidas válidas manualmente ou importe um CSV/TXT do BreatheLink para calcular S-Index, valor predito e curvas.</p>
        </div>
      </div>

      <div className="rounded-lg border border-dashed p-4 text-sm text-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-medium">
            <FileUp className="h-4 w-4" />
            Importar CSV/TXT do BreatheLink
          </div>
          <div className="flex items-center gap-2">
            {analysis && (
              <button type="button" className="rounded border px-3 py-1 text-xs font-medium text-slate-700 hover:bg-white" onClick={clearImportedFile}>
                Remover arquivo
              </button>
            )}
            <label className="cursor-pointer rounded bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
              Selecionar arquivo
              <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFileChange} />
            </label>
          </div>
        </div>
        <div className="mt-2 text-xs text-slate-500">O importador continua ativo. Sem arquivo, você pode lançar as medidas manualmente abaixo.</div>
        {fileMeta && <div className="mt-2 text-xs text-emerald-700">Arquivo carregado: {fileMeta.name} ({Math.round(fileMeta.size / 1024)} KB)</div>}
        {loading && <div className="mt-2 text-xs text-blue-600">Processando arquivo...</div>}
        {error && <div className="mt-2 text-xs text-red-600">Erro: {error}</div>}
      </div>

      {!analysis && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Medições de S-Index (cmH2O)</h3>
            <button type="button" onClick={() => setMeasurements((current) => [...current, ''])} className="inline-flex items-center gap-2 text-sm text-indigo-600">
              <Plus className="h-4 w-4" />
              Adicionar medida
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {measurements.map((value, index) => (
              <label key={`sindex-measure-${index}`} className="text-sm">
                {index + 1}a medida
                <div className="mt-1 flex items-center gap-2">
                  <input className="w-full rounded border px-2 py-1" inputMode="decimal" value={value} onChange={(event) => updateMeasurement(index, event.target.value)} />
                  {measurements.length > 3 && (
                    <button type="button" onClick={() => setMeasurements((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-2 text-slate-500 hover:text-red-600" aria-label="Remover medida">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="text-xs text-slate-500">Registre pelo menos 3 medidas válidas. O melhor valor será usado como S-Index final.</div>
        </div>
      )}

      <div className="space-y-2 rounded-lg border bg-white p-3 shadow-sm">
        <div className="text-xs uppercase text-slate-500">Referência para valor predito</div>
        <select className="w-full rounded border bg-white px-2 py-1 text-sm" value={selectedReferenceEquation} onChange={(event) => setSelectedReferenceEquation(event.target.value as SIndexReferenceKey)}>
          {referenceOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
        {selectedReferenceEquation === 'reis_2025_hgdul' && (
          <div className="grid items-end gap-2 md:grid-cols-2">
            <label className="text-sm">
              Handgrip para equação de Reis (kg)
              <input
                className="mt-1 w-full rounded border px-2 py-1"
                inputMode="decimal"
                value={manualHandgripInput}
                onChange={(event) => setManualHandgripInput(event.target.value)}
                placeholder={effectiveReferenceHandgripKg != null ? `Disponível: ${effectiveReferenceHandgripKg.toFixed(1)} kg` : 'Ex.: 32,5'}
              />
            </label>
            <div className="text-xs text-slate-500">
              {manualHandgripKg != null
                ? `Usando valor manual: ${manualHandgripKg.toFixed(1)} kg.`
                : effectiveReferenceHandgripKg != null
                  ? `Usando valor de Handgrip disponível: ${effectiveReferenceHandgripKg.toFixed(1)} kg.`
                  : 'Sem Handgrip disponível para esta equação.'}
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="S-Index" value={estimationSummary?.maxPressure != null ? `${estimationSummary.maxPressure.toFixed(1)} cmH2O` : '-'} emphasis />
        <Metric label="Predito" value={estimationSummary?.predictedSIndex != null ? `${estimationSummary.predictedSIndex.toFixed(1)} cmH2O` : '-'} />
        <Metric label="% do predito" value={estimationSummary?.percentPredicted != null ? `${estimationSummary.percentPredicted.toFixed(1)}%` : '-'} />
        <Metric label={analysis ? 'Respirações' : 'Medidas'} value={String(effectiveBreathsAnalyzed || 0)} />
      </div>

      {estimationSummary && (
        <>
          <div className="text-xs text-slate-600">
            {analysis
              ? `Fórmula BreatheLink: ${estimationSummary.estimation.formula}. Pressão bruta CSV: ${estimationSummary.maxPressureRaw.toFixed(1)} cmH2O.`
              : `Modo manual: maior medida registrada ${estimationSummary.maxPressure.toFixed(1)} cmH2O.`}
            {' '}Referência: {estimationSummary.referenceLabel}.{' '}
            {estimationSummary.llnSIndex != null ? `Limite inferior esperado: ${estimationSummary.llnSIndex.toFixed(1)} cmH2O.` : ''}
          </div>

          <div className={`rounded-lg border p-3 text-sm ${interpretation.tone === 'good' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : interpretation.tone === 'alert' ? 'border-rose-200 bg-rose-50 text-rose-900' : interpretation.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-900'}`}>
            <div className="font-semibold">Interpretação clínica: {interpretation.title}</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">{interpretation.lines.map((line, index) => <li key={`sindex-interp-${index}`}>{line}</li>)}</ul>
          </div>
        </>
      )}

      {analysis && estimationSummary && (
        <>
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-700">Curvas de pressão sobrepostas</div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setSelectedBreathNumbers(allBreathNumbers)}>Todas</button>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {allBreathNumbers.map((breathNumber) => (
                <label key={breathNumber} className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 ${activeBreathNumbers.includes(breathNumber) ? 'border-indigo-200 bg-indigo-50 text-indigo-800' : 'bg-white text-slate-700'}`}>
                  <input type="checkbox" className="accent-indigo-600" checked={activeBreathNumbers.includes(breathNumber)} onChange={() => toggleBreathSelection(breathNumber)} />
                  Curva {breathNumber}
                </label>
              ))}
            </div>

            {editingBreath != null && (
              <CurveEditPanel
                breathNumber={editingBreath}
                breathNumbers={allBreathNumbers}
                edit={breathEdits[editingBreath]}
                onSelect={setEditingBreath}
                onUpdate={(updates) => updateBreathEdit(editingBreath, updates)}
                onReset={() => resetBreathEdit(editingBreath)}
                onResetAll={() => setBreathEdits({})}
              />
            )}

            <div className="rounded-lg border bg-white p-3 shadow-sm" ref={chartRef}>
              <OverlappedPressureChart data={overlappedPressureData} breaths={visibleBreaths} height={280} />
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Resp.</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-left">Tins</th>
                  <th className="px-3 py-2 text-left">Pico pressão</th>
                  <th className="px-3 py-2 text-left">Pico fluxo</th>
                  <th className="px-3 py-2 text-left">Pico volume</th>
                  <th className="px-3 py-2 text-left">Trabalho</th>
                </tr>
              </thead>
              <tbody>
                {analysis.metrics.map((metric) => (
                  <tr key={metric.breathNumber} className="border-t">
                    <td className="px-3 py-2">{metric.breathNumber}</td>
                    <td className="px-3 py-2">{metric.targetResistance != null ? metric.targetResistance.toFixed(1) : '-'}</td>
                    <td className="px-3 py-2">{metric.tins.toFixed(2)}</td>
                    <td className="px-3 py-2">{metric.peakPressure.toFixed(2)}</td>
                    <td className="px-3 py-2">{metric.peakFlow.toFixed(2)}</td>
                    <td className="px-3 py-2">{metric.peakVolume.toFixed(2)}</td>
                    <td className="px-3 py-2">{metric.totalWork.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {exportingCharts && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">Exportando gráfico para impressão...</div>}

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-slate-600 hover:text-slate-800" onClick={onCancel}>Cancelar</button>
        <button type="button" className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50" disabled={!canFinish} onClick={handleFinish}>
          Concluir S-Index <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const Metric = ({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) => (
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${emphasis ? 'text-indigo-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

const CurveEditPanel = ({
  breathNumber,
  breathNumbers,
  edit,
  onSelect,
  onUpdate,
  onReset,
  onResetAll,
}: {
  breathNumber: number;
  breathNumbers: number[];
  edit?: { trimStart: number; trimEnd: number };
  onSelect: (breathNumber: number) => void;
  onUpdate: (updates: Partial<{ trimStart: number; trimEnd: number }>) => void;
  onReset: () => void;
  onResetAll: () => void;
}) => {
  const trimStart = edit?.trimStart ?? 0;
  const trimEnd = edit?.trimEnd ?? 0;
  const buildSafeValue = (value: number, other: number) => {
    const safe = Number.isFinite(value) ? Math.min(90, Math.max(0, value)) : 0;
    return safe + other > 90 ? Math.max(0, 90 - other) : safe;
  };

  return (
    <div className="space-y-2 rounded-lg border bg-white p-3 shadow-sm">
      <div className="text-xs uppercase text-slate-500">Editar curva especifica</div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label>
          Curva
          <select className="mt-1 rounded border px-2 py-1" value={breathNumber} onChange={(event) => onSelect(Number(event.target.value))}>
            {breathNumbers.map((item) => <option key={item} value={item}>Curva {item}</option>)}
          </select>
        </label>
        <label>
          Cortar inicio (%)
          <input className="mt-1 w-24 rounded border px-2 py-1" type="number" min={0} max={90} value={trimStart} onChange={(event) => onUpdate({ trimStart: buildSafeValue(Number(event.target.value), trimEnd) })} />
        </label>
        <label>
          Cortar final (%)
          <input className="mt-1 w-24 rounded border px-2 py-1" type="number" min={0} max={90} value={trimEnd} onChange={(event) => onUpdate({ trimEnd: buildSafeValue(Number(event.target.value), trimStart) })} />
        </label>
        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={onReset}>Resetar curva</button>
        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={onResetAll}>Resetar tudo</button>
      </div>
    </div>
  );
};
